import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { fetchData, postData } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import { ReasonCaptureModal } from '../Common/ReasonCaptureModal';
import { Guard } from '../Common/Guard';
import { PERMS } from '../../Utils/Permissions';
import PageLayout from '../Layout/PageLayout';
import { Card, CardTitleBar, CardBody, FormField, TextArea, TextInput, Button, StatusBadge, Spinner } from '../UI';
import { tokens } from '../../theme/tokens';

const DetailRow = styled.div`
  display: flex;
  gap: ${tokens.spacing[3]};
  padding: ${tokens.spacing[3]} 0;
  border-bottom: 1px solid ${tokens.colors.divider};
  font-size: ${tokens.typography.size.base};
  color: ${tokens.colors.body};
  &:last-child { border-bottom: 0; }
`;

const DetailLabel = styled.span`
  flex: 0 0 140px;
  font-weight: ${tokens.typography.weight.semibold};
  color: ${tokens.colors.muted};
`;

const DetailValue = styled.div`
  flex: 1;
  min-width: 0;
  color: ${tokens.colors.heading};
  code {
    font-family: ${tokens.typography.fonts.mono};
    font-size: ${tokens.typography.size.sm};
    background: ${tokens.colors.surfaceSunken};
    padding: 2px 6px;
    border-radius: ${tokens.radii.sm};
  }
`;

const Pre = styled.pre`
  white-space: pre-wrap;
  margin: ${tokens.spacing[2]} 0 0;
  padding: ${tokens.spacing[4]};
  background: ${tokens.colors.surfaceSunken};
  border: 1px solid ${tokens.colors.border};
  border-radius: ${tokens.radii.md};
  font-family: ${tokens.typography.fonts.mono};
  font-size: ${tokens.typography.size.sm};
  color: ${tokens.colors.body};
`;

export default function ComplaintDetails() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'triage' | 'resolve' | 'refund' | null
  const [triageNote, setTriageNote] = useState('');
  const [resolution, setResolution] = useState('');
  const [refundKES, setRefundKES] = useState('');

  const load = useCallback(() => {
    fetchData(Endpoints.GET_COMPLAINT(id))
      .then((r) => r.json())
      .then((res) => setC((res && res.data) || res));
  }, [id]);

  useEffect(load, [load]);

  if (!c || !c.id) return <PageLayout title="Complaint"><Spinner center label="Loading…" /></PageLayout>;

  const submitTriage = (reason) => {
    const fd = new FormData();
    fd.append('triage_note', triageNote);
    fd.append('reason', reason);
    postData(Endpoints.TRIAGE_COMPLAINT(c.id), fd)
      .then(() => { setPendingAction(null); setTriageNote(''); load(); });
  };

  const submitResolve = (reason) => {
    const fd = new FormData();
    fd.append('resolution', resolution);
    fd.append('reason', reason);
    postData(Endpoints.RESOLVE_COMPLAINT(c.id), fd)
      .then(() => { setPendingAction(null); setResolution(''); load(); });
  };

  const submitRefund = (reason) => {
    const cents = Math.round(parseFloat(refundKES) * 100);
    if (!Number.isFinite(cents) || cents <= 0) return;
    const fd = new FormData();
    fd.append('amount_cents', String(cents));
    fd.append('reason', reason);
    postData(Endpoints.REFUND_COMPLAINT(c.id), fd)
      .then(() => { setPendingAction(null); setRefundKES(''); load(); });
  };

  const disableTriage = c.status === 'resolved';
  const disableResolve = c.status === 'resolved';
  const disableRefund = c.status === 'resolved' && c.refund_cents > 0;

  return (
    <PageLayout title="Complaint">
      <Card padded={false} style={{ marginBottom: tokens.spacing[5] }}>
        <CardTitleBar title="Complaint details" actions={<StatusBadge status={c.status} />} />
        <CardBody>
          <DetailRow>
            <DetailLabel>When</DetailLabel>
            <DetailValue>{new Date(c.created_at).toLocaleString()}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Category</DetailLabel>
            <DetailValue>{c.category || '—'}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>User</DetailLabel>
            <DetailValue><code>{c.user_id}</code></DetailValue>
          </DetailRow>
          {c.request_id && (
            <DetailRow>
              <DetailLabel>Request</DetailLabel>
              <DetailValue><Link to={`/request/${c.request_id}`}>{c.request_id}</Link></DetailValue>
            </DetailRow>
          )}
          <DetailRow>
            <DetailLabel>Description</DetailLabel>
            <DetailValue><Pre style={{ marginTop: 0 }}>{c.description}</Pre></DetailValue>
          </DetailRow>
          {c.triage_note && (
            <DetailRow>
              <DetailLabel>Triage note</DetailLabel>
              <DetailValue><Pre style={{ marginTop: 0 }}>{c.triage_note}</Pre></DetailValue>
            </DetailRow>
          )}
          {c.resolution && (
            <DetailRow>
              <DetailLabel>Resolution</DetailLabel>
              <DetailValue><Pre style={{ marginTop: 0 }}>{c.resolution}</Pre></DetailValue>
            </DetailRow>
          )}
          {c.refund_cents > 0 && (
            <DetailRow>
              <DetailLabel>Refund issued</DetailLabel>
              <DetailValue>KES {(c.refund_cents / 100).toFixed(2)}</DetailValue>
            </DetailRow>
          )}
          {c.resolved_at && (
            <DetailRow>
              <DetailLabel>Resolved at</DetailLabel>
              <DetailValue>{new Date(c.resolved_at).toLocaleString()}</DetailValue>
            </DetailRow>
          )}
          {c.handled_by_id && (
            <DetailRow>
              <DetailLabel>Handled by</DetailLabel>
              <DetailValue><code>{c.handled_by_id}</code></DetailValue>
            </DetailRow>
          )}
        </CardBody>
      </Card>

      <Card padded={false}>
        <CardTitleBar title="Actions" />
        <CardBody>
          <Guard permission={PERMS.REQUEST_WRITE}>
            <div style={{ marginBottom: tokens.spacing[5] }}>
              <FormField label="Triage note" htmlFor="triage-note">
                <TextArea
                  id="triage-note"
                  rows={2}
                  value={triageNote}
                  onChange={(e) => setTriageNote(e.target.value)}
                />
              </FormField>
              <Button
                disabled={disableTriage || !triageNote.trim()}
                onClick={() => setPendingAction('triage')}
              >
                Triage
              </Button>
            </div>

            <div style={{ marginBottom: tokens.spacing[5] }}>
              <FormField label="Resolution" htmlFor="resolution-note">
                <TextArea
                  id="resolution-note"
                  rows={2}
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                />
              </FormField>
              <Button
                disabled={disableResolve || !resolution.trim()}
                onClick={() => setPendingAction('resolve')}
              >
                Resolve
              </Button>
            </div>
          </Guard>

          <Guard permission={PERMS.REFUND_ISSUE}>
            <div style={{ marginBottom: tokens.spacing[3] }}>
              <FormField label="Refund amount (KES)" htmlFor="refund-amount">
                <TextInput
                  id="refund-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={refundKES}
                  onChange={(e) => setRefundKES(e.target.value)}
                  style={{ maxWidth: 220 }}
                />
              </FormField>
              <Button
                variant="danger"
                disabled={disableRefund || !refundKES}
                onClick={() => setPendingAction('refund')}
              >
                Issue refund
              </Button>
            </div>
          </Guard>
        </CardBody>
      </Card>

      {pendingAction === 'triage' && (
        <ReasonCaptureModal
          title="Triage complaint"
          onCancel={() => setPendingAction(null)}
          onConfirm={submitTriage}
        />
      )}
      {pendingAction === 'resolve' && (
        <ReasonCaptureModal
          title="Resolve complaint"
          onCancel={() => setPendingAction(null)}
          onConfirm={submitResolve}
        />
      )}
      {pendingAction === 'refund' && (
        <ReasonCaptureModal
          title="Issue refund"
          onCancel={() => setPendingAction(null)}
          onConfirm={submitRefund}
        />
      )}
    </PageLayout>
  );
}
